import { useEffect, useId, useRef, type ReactNode } from 'react';
import { Icon } from './Icon';

// Escape closes only the top-most dialog when several are stacked (e.g. a photo over a record).
const stack: string[] = [];

export function useEscapeToClose(onClose: (() => void) | undefined, enabled = true) {
  const key = useId();
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    stack.push(key);
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape' && enabled && stack[stack.length - 1] === key) closeRef.current?.(); };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); stack.splice(stack.indexOf(key), 1); };
  }, [key, enabled]);
}

interface ModalProps {
  id?: string;
  className?: string;
  /** Dialog width: sm for short forms and confirmations, md (default) for record forms, lg for boards and long forms. */
  size?: 'sm' | 'md' | 'lg';
  backdropClassName?: string;
  labelledBy?: string;
  onClose?: () => void;
  /** Hide the × button and ignore Escape and backdrop clicks (e.g. a required password change). */
  dismissable?: boolean;
  children: ReactNode;
}

export function Modal({ id, className = '', size = 'md', backdropClassName = '', labelledBy, onClose, dismissable = true, children }: ModalProps) {
  const pressStartedOnBackdrop = useRef(false);
  useEscapeToClose(onClose, dismissable);
  return (
    <div
      className={`modal-backdrop ${backdropClassName}`.trim()}
      id={id}
      onPointerDown={(event) => { pressStartedOnBackdrop.current = event.target === event.currentTarget; }}
      onClick={(event) => { if (dismissable && pressStartedOnBackdrop.current && event.target === event.currentTarget) onClose?.(); }}
    >
      <div className={`modal modal-${size} ${className}`.trim()} role="dialog" aria-modal="true" aria-labelledby={labelledBy}>
        {dismissable && onClose && <button type="button" className="icon-button modal-close" onClick={onClose} aria-label="Close"><Icon name="close" size={20} /></button>}
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}
